import { useState } from 'react';

interface FeatureToggleProps {
  featureFlags: Record<string, boolean>;
  onToggle: (key: string, value: boolean) => void;
  className?: string;
}

export const FeatureToggle = ({ featureFlags, onToggle, className = '' }: FeatureToggleProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!featureFlags || Object.keys(featureFlags).length === 0) {
    return (
      <div className={`rounded-md border border-slate-300 bg-slate-50 p-3 text-sm text-slate-500 ${className}`}>
        No feature flags available
      </div>
    );
  }

  const toggleFeature = (key: string) => {
    onToggle(key, !featureFlags[key]);
  };

  const formatFeatureName = (key: string) => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Sort feature flags alphabetically by key
  const sortedFeatureFlags = Object.entries(featureFlags).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className={`rounded-md border border-slate-300 bg-white ${className}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between p-3 text-left hover:bg-slate-50 transition-colors"
        data-testid="feature-flags-dropdown"
      >
        <span className="text-sm font-medium text-slate-700">Feature Flags</span>
        <span className={`transform text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>

      {isExpanded && (
        <div className="border-t border-slate-200">
          <div className="max-h-[200px] overflow-y-auto p-3 space-y-3">
            {sortedFeatureFlags.map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <label htmlFor={`toggle-${key}`} className="text-sm text-slate-600 flex-1 cursor-pointer">
                  {formatFeatureName(key)}
                </label>
                <button
                  id={`toggle-${key}`}
                  onClick={() => toggleFeature(key)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${value ? 'bg-blue-600' : 'bg-slate-300'
                    }`}
                  role="switch"
                  aria-checked={value}
                  aria-labelledby={`toggle-${key}`}
                  aria-label={`Toggle ${formatFeatureName(key)}`}
                  data-testid={`feature-toggle-${key}`}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-200 ${value ? 'translate-x-5' : 'translate-x-1'
                      }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};